// src/components/adminLists/AdminChallenges.jsx
//
// /admin/lists, tab « Défis »: the « Défi du mois » of the app (backend
// routes/adminChallenges.ts). The admin opens a challenge for some dates
// (and a city: only its places can be proposed); phones that rated a place
// there on site propose it, everyone votes. An entry can be taken out here.
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import {
  listChallenges, createChallenge, deleteChallenge, deleteEntry, challengeErrorText,
} from '../../utils/adminChallengesApi';

const STATE = { upcoming: ['À venir', 'default'], open: ['En cours', 'success'], closed: ['Terminé', 'default'] };
const day = (d) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
// <input type="date"> values: the start at 00:00, the end at 23:59, local time.
const toIso = (v, end) => new Date(`${v}T${end ? '23:59:59' : '00:00:00'}`).toISOString();

export default function AdminChallenges({ getToken }) {
  const [challenges, setChallenges] = useState(null);
  const [msg, setMsg] = useState({});
  const [form, setForm] = useState({ title: '', city: '', description: '', start: '', end: '' });

  const refresh = useCallback(
    () => listChallenges(getToken()).then((r) => setChallenges(r.challenges)).catch((e) => setMsg({ error: challengeErrorText(e) })),
    [getToken]);
  useEffect(() => { refresh(); }, [refresh]);

  async function run(fn, done) {
    setMsg({});
    try { await fn(); setMsg({ info: done }); refresh(); } catch (e) { setMsg({ error: challengeErrorText(e) }); }
  }
  const create = () => run(async () => {
    if (!form.start || !form.end) throw new Error('INVALID_DATES');
    await createChallenge({
      title: form.title, city: form.city, description: form.description,
      startsAt: toIso(form.start, false), endsAt: toIso(form.end, true),
    }, getToken());
    setForm({ title: '', city: '', description: '', start: '', end: '' });
  }, 'Défi créé.');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (!challenges) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  return (
    <>
      {msg.error && <Alert severity="warning" sx={{ mb: 2 }}>{msg.error}</Alert>}
      {msg.info && <Alert severity="success" sx={{ mb: 2 }}>{msg.info}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Nouveau défi</Typography>
        <Stack spacing={1.5}>
          <TextField label="Titre" placeholder="Le meilleur tartare de Lyon" value={form.title} onChange={set('title')} inputProps={{ maxLength: 120 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField label="Ville (facultatif)" value={form.city} onChange={set('city')} helperText="Seuls les établissements de cette ville pourront être proposés" fullWidth />
            <TextField label="Début" type="date" value={form.start} onChange={set('start')} InputLabelProps={{ shrink: true }} fullWidth />
            <TextField label="Fin" type="date" value={form.end} onChange={set('end')} InputLabelProps={{ shrink: true }} fullWidth />
          </Stack>
          <TextField label="Description (facultatif)" value={form.description} onChange={set('description')} multiline minRows={2} inputProps={{ maxLength: 500 }} />
          <Box><Button variant="contained" onClick={create} disabled={!form.title.trim()}>Créer le défi</Button></Box>
        </Stack>
      </Paper>

      {!challenges.length && <Typography color="text.secondary">Aucun défi.</Typography>}
      {challenges.map((c) => (
        <Paper key={c.id} variant="outlined" sx={{ p: 2, mb: 2 }} data-testid={`challenge-${c.id}`}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontWeight: 700 }}>{c.title}</Typography>
            <Chip size="small" label={STATE[c.state][0]} color={STATE[c.state][1]} />
            {c.city && <Chip size="small" variant="outlined" label={c.city} />}
            <Typography variant="body2" color="text.secondary">{day(c.startsAt)} → {day(c.endsAt)} · {c.total} vote{c.total > 1 ? 's' : ''}</Typography>
            <Button size="small" color="error" sx={{ ml: 'auto' }}
              onClick={() => window.confirm(`Supprimer « ${c.title} » et ses votes ?`) && run(() => deleteChallenge(c.id, getToken()), 'Défi supprimé.')}>
              Supprimer
            </Button>
          </Stack>
          {c.entries.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Aucun établissement proposé.</Typography>}
          {c.entries.map((e) => (
            <Stack key={e.id} direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <Typography sx={{ width: 32, fontWeight: 800 }}>#{e.rank}</Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{e.facility.name} · {e.votes} vote{e.votes > 1 ? 's' : ''}</Typography>
                <Typography variant="caption" color="text.secondary">{e.facility.address} · proposé par {e.proposedBy.name}</Typography>
              </Box>
              <Button size="small" onClick={() => window.confirm(`Retirer « ${e.facility.name} » du défi ?`) && run(() => deleteEntry(e.id, getToken()), 'Établissement retiré.')}>
                Retirer
              </Button>
            </Stack>
          ))}
        </Paper>
      ))}
    </>
  );
}
